import { format } from 'date-fns';
import findLastIndex from 'lodash.findlastindex';
import type { TFunction } from 'next-i18next';

import { JobApplication, WorkerJobPositionStatus } from '@restworld/utility-types';
import { formatDateIn, getDateFnsLocale } from '@restworld/utils-common';

import type { PreInterview } from '../pre-interview';
import { TimeLineKeysType, ApplicationStateType, IApplicationTimelineState } from './application-details.constants';

export type BuildApplicationTimelineArgs = {
  /** The base list, already translated, from `useApplicationTimelineStateList`. */
  base: Partial<IApplicationTimelineState[]>;
  isSpontaneousApplication: boolean;
  applicationState: ApplicationStateType;
  applyJobStatusData: Partial<JobApplication>;
  listWorkerJobApplicationsHistory: WorkerJobPositionStatus[];
  /** Step ids reached before the application was discarded; only ever membership-tested. */
  accomplishedSteps: string[];
  /** Already-composed rejection sentence, or '' when the application was not rejected. */
  discardStatement: string;
  /** The interview with Serena, or `undefined` when this application has none. */
  preInterview?: PreInterview;
  t: TFunction;
  /** next-i18next locale of the reader, e.g. 'it' | 'en'. */
  locale?: string;
};

/**
 * Enriches the translated base timeline with dates and the rejection sentence,
 * then filters it down to the steps that apply to this application.
 *
 * This is a plain function, not a jotai atom, for two reasons. It needs `t`,
 * which only exists in the React tree; and the value it produces depends on the
 * active locale, so it has to be recomputed on a language switch rather than
 * memoised in a store.
 *
 * Every date here is formatted in the reader's locale. It used not to be: the
 * trial-period start date was formatted with no locale at all while its end date
 * used Italian, so an Italian reader was shown half an English date.
 */
export const buildApplicationTimeline = ({
  base,
  isSpontaneousApplication,
  applicationState,
  applyJobStatusData,
  listWorkerJobApplicationsHistory,
  accomplishedSteps,
  discardStatement,
  preInterview,
  t,
  locale,
}: BuildApplicationTimelineArgs): Partial<IApplicationTimelineState[]> => {
  if (!base.length) return [];

  const dateFnsLocale = getDateFnsLocale(locale);

  const getIndexData = (statusKey: TimeLineKeysType[]) => {
    const index = findLastIndex(
      listWorkerJobApplicationsHistory,
      (history) => statusKey.indexOf(history.status.key as TimeLineKeysType) > -1
    );
    return listWorkerJobApplicationsHistory[index];
  };

  // Steps whose date is simply "when this status was reached".
  const historyDateSteps = [
    'application-submitted',
    'application-submitted-to-the-company',
    'hired',
    'rejected',
    'not-interested',
  ];

  let list = base.map((item) => {
    if (historyDateSteps.includes(item.id)) {
      const data = getIndexData(item.statusKey as TimeLineKeysType[]);
      const enriched = {
        ...item,
        headText: {
          ...item.headText,
          subLabel: data?.inserted_at ? formatDateIn(data.inserted_at, locale) : '',
        },
      };
      if (item.id !== 'rejected') return enriched;
      return {
        ...enriched,
        bodyText: { ...item.bodyText, mainLabel: discardStatement },
      };
    }

    if (item.id === 'application-under-evaluation') {
      const lastInterview = applyJobStatusData.interview_date;
      const unixTime = new Date(lastInterview).valueOf();
      return {
        ...item,
        headText: {
          ...item.headText,
          // Date and time are separate parameters so each language can join them
          // its own way: Italian puts "alle" between them, English "at".
          subLabel: lastInterview
            ? t('job-application-page:show_up_on', {
                date: format(unixTime, 'dd MMM yyyy', { locale: dateFnsLocale }),
                time: format(unixTime, 'HH:mm', { locale: dateFnsLocale }),
              })
            : ``,
        },
      };
    }

    if (item.id === 'trial-period') {
      const startDate = applyJobStatusData.stage_start_date;
      const endDate = applyJobStatusData.stage_end_date;
      return {
        ...item,
        headText: {
          ...item.headText,
          subLabel:
            startDate && endDate
              ? t('job-application-page:trial_period_from_to', {
                  start: formatDateIn(startDate, locale),
                  end: formatDateIn(endDate, locale),
                })
              : ``,
        },
      };
    }

    if (item.id === 'spontaneous-application') {
      const insertedAt = applyJobStatusData.inserted_at;
      return {
        ...item,
        headText: {
          ...item.headText,
          subLabel: insertedAt ? formatDateIn(insertedAt, locale) : ``,
        },
      };
    }

    return item;
  });

  // The step only exists for an application that has an interview in the app: whether
  // `assessment_channel` says so, and whether there is a status to draw.
  if (!preInterview) list = list.filter((item) => item.id !== 'interview');

  if (isSpontaneousApplication) {
    if (applicationState === 'rejected')
      list = list.filter((item) => ['spontaneous-application', 'rejected'].includes(item.id));
    else if (applicationState === 'not-interested')
      list = list.filter((item) => ['spontaneous-application', 'not-interested'].includes(item.id));
    else list = list.filter((item) => item.id === 'spontaneous-application');
    return list;
  }

  if (applicationState === 'rejected')
    list = list.filter((item) => [...accomplishedSteps, 'rejected'].includes(item.id));
  else if (applicationState === 'accepted' || applicationState === 'pending')
    list = list.filter((item) => !['rejected', 'spontaneous-application', 'not-interested'].includes(item.id));
  else if (applicationState === 'not-interested') list = list.filter((item) => item.id === 'not-interested');
  else list = [];

  return list;
};
